import { Close, ExpandLess, ExpandMore } from '@mui/icons-material';
import { Box, Divider, IconButton, Paper, Stack, Typography } from '@mui/material';
import { FileAccessLevel, FileEntityType, SystemFileType } from 'middleware-types';
import { createContext, Fragment, ReactNode, useContext, useEffect, useState } from 'react';
import { theme } from 'utils/theme';
import { v4 as uuid4 } from 'uuid';
import { UploadsDrawerRow } from './uploads-drawer-row';
import { UploadState } from 'utils/fileUtils';

export interface UploadsDrawerItem {
	key: string;
	file: File;
	relatedEntityId: string;
	relatedEntityType: FileEntityType;
	systemFileType: SystemFileType;
	accessLevel: FileAccessLevel;
	updatesFileId?: string;
	subtitle: string;
	url: string;
	onCompleted: (uploadToken: string) => Promise<boolean>;
	state: UploadState;
}

export type UploadsDrawerRequest = Omit<UploadsDrawerItem, 'key' | 'state'>;

interface UploadsDrawerContextState {
	drawerOpen: boolean;
	uploadFiles: (items: UploadsDrawerRequest | UploadsDrawerRequest[]) => void;
}

const UploadsDrawerContext = createContext<UploadsDrawerContextState | undefined>(undefined);

export const UploadsDrawer = ({ children }: { children: ReactNode }) => {
	const [open, setOpen] = useState(false);
	const [files, setFiles] = useState<UploadsDrawerItem[]>([]);

	const numCompleted = files.filter((file) => file.state === UploadState.Success).length;

	const numErrored = files.filter((file) => file.state === UploadState.Error).length;

	const [showNumErroredBar, setShowNumErroredBar] = useState<boolean>(numErrored > 0);

	useEffect(() => {
		setShowNumErroredBar(numErrored > 0);
	}, [numErrored]);

	const canClose = !files.some(
		(file) => file.state === UploadState.Pending || file.state === UploadState.Loading
	);

	const setState = (key: string, state: UploadState) =>
		setFiles((current) =>
			current.map((file) => {
				if (file.key === key)
					return {
						...file,
						state,
					};
				return file;
			})
		);

	const uploadFiles = (_items: UploadsDrawerRequest | UploadsDrawerRequest[]) => {
		const items = Array.isArray(_items) ? _items : [_items];
		const newFiles: UploadsDrawerItem[] = items.map((item) => ({
			...item,
			key: uuid4(),
			state: UploadState.Pending,
		}));
		setFiles((currentFiles) => [...currentFiles, ...newFiles]);
		setOpen(true);
	};

	return (
		<UploadsDrawerContext.Provider value={{ drawerOpen: files.length > 0, uploadFiles }}>
			{children}
			{files.length > 0 && (
				<Stack
					position="fixed"
					overflow="hidden"
					bottom={0}
					right={{ xs: 0, sm: 12 }}
					width={{ xs: '100vw', sm: 600 }}
					borderRadius={2}
					sx={{ borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}
					component={Paper}
					zIndex={theme.zIndex.drawer}>
					<Stack direction="row" alignItems="center" bgcolor="neutral.50" px={2} py={1}>
						<Box flex={1}>
							<Typography variant="h3" display="inline">
								Uploads{' '}
							</Typography>
							<Typography variant="subtitle1" display="inline">
								({numCompleted} of {files.length} completed)
							</Typography>
						</Box>
						<IconButton onClick={() => setOpen(!open)}>
							{open ? <ExpandMore /> : <ExpandLess />}
						</IconButton>
						{canClose && (
							<IconButton onClick={() => setFiles([])}>
								<Close />
							</IconButton>
						)}
					</Stack>
					{showNumErroredBar && (
						<Stack
							direction="row"
							alignItems="center"
							justifyContent="space-between"
							px={2}
							sx={{
								backgroundColor: 'error.50',
								borderTop: '1px solid',
								borderColor: 'divider',
							}}>
							<Typography variant="subtitle1" display="inline" color="error.500">
								{numErrored} file{numErrored > 1 ? 's' : ''} failed to upload
							</Typography>
							<IconButton onClick={() => setShowNumErroredBar(false)}>
								<Close />
							</IconButton>
						</Stack>
					)}
					<Box
						maxHeight="50vh"
						overflow="auto"
						borderTop="1px solid"
						borderColor="divider"
						display={open ? 'block' : 'none'}>
						{files.map((file, index) => (
							<Fragment key={file.key}>
								{index !== 0 && <Divider />}
								<UploadsDrawerRow
									item={file}
									setState={(state: UploadState) => setState(file.key, state)}
								/>
							</Fragment>
						))}
					</Box>
				</Stack>
			)}
		</UploadsDrawerContext.Provider>
	);
};

export const useUploadsDrawer = () => {
	const value = useContext(UploadsDrawerContext);
	if (value === undefined)
		throw new Error('useUploadsDrawerContext must be used inside a UploadsDrawer component');
	return value;
};
